import { useActiveFamily } from "../../context/ActiveFamilyContext";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";

interface FamilySwitcherModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function FamilySwitcherModal({
  isOpen, onClose }: FamilySwitcherModalProps) {
  const { t } = useTranslation();
  const { activeFamily, availableFamilies, setActiveFamily } = useActiveFamily();

  if (!isOpen) return null;

  const handleSelectFamily = (familyId: string) => {
    const selected = availableFamilies.find((f) => f.id === familyId);
    if (selected) {
      setActiveFamily(selected);
      onClose();
    }
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-[9999] animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Modal - Adaptado para mobile */}
      <div
        className="fixed inset-x-4 top-1/2 -translate-y-1/2 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:w-full sm:max-w-md rounded-[24px] shadow-sm z-[9999] animate-in zoom-in-95 duration-200 max-h-[85vh] flex flex-col"
        style={{ backgroundColor: '#fff5eb' }}
      >
        {/* Header */}
        <div
          className="px-4 sm:px-6 py-4 sm:py-5 rounded-t-[24px] flex-shrink-0"
          style={{ backgroundColor: '#055590' }}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
                <span className="text-xl sm:text-2xl">👨‍👩‍👧‍👦</span>
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-base sm:text-xl font-black text-white truncate">
                  {t('groups.cambiarde')}
                </h2>
                <p className="text-xs sm:text-sm text-white/80 truncate">
                  {t('groups.seleccionauno')}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-2xl transition-colors flex-shrink-0"
              aria-label={t('groups.cerrar')}
            >
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Lista de familias - Scrollable */}
        <div className="p-3 sm:p-4 space-y-2 overflow-y-auto flex-1">
          {availableFamilies.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                {t('groups.nofamiliasdisponibles')}
              </p>
            </div>
          ) : (
            availableFamilies.map((family) => {
              const isActive = activeFamily?.id === family.id;
              const isGroup = (family as any).type === 'group';
              const icon = isGroup ? '👥' : '🏠';
              const label = isGroup ? 'Equipo' : 'Familia';

              return (
                <button
                  key={family.id}
                  onClick={() => handleSelectFamily(family.id)}
                  className="w-full flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-2xl transition-all shadow-sm"
                  style={{
                    backgroundColor: isActive ? '#ef6e6b' : '#ffffff',
                    opacity: isActive ? 1 : 0.8
                  }}
                >
                  <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center flex-shrink-0 bg-white dark:bg-gray-900">
                    <span className="text-xl sm:text-2xl">{icon}</span>
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <h3 className={`font-black text-sm sm:text-base truncate ${
                      isActive ? 'text-white' : 'text-gray-900 dark:text-white'
                    }`}>
                      {family.name}
                    </h3>
                    <p className={`text-xs sm:text-sm truncate ${
                      isActive ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {t('groups.' + label)} {isGroup ? '' : `• ${family.name}`}
                    </p>
                  </div>
                  {isActive && (
                    <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
    </>,
    document.body
  );
}
